import React, { useEffect, useState } from 'react'
import { Modal, Box, Typography, Button, IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import { ReactComponent as CloseIcon } from 'assets/svgs/actions/close.svg'
import { useLookupContext } from 'contexts/LookupContext'
import { loader } from 'graphql.macro'
import client from 'utils/apolloClient'
import { useNavigate } from 'react-router-dom'
const SITECORE_CONTENT_QUERY = loader('./SitecoreContent.query.graphql')

const StyledModalBox = styled(Box)(({ theme }) => ({
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '90%',
	maxWidth: '750px',
	minHeight: '775px',
	padding: theme.spacing(7, 4),
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	borderRadius: theme.shape.borderRadius,
	borderBottom: `2px solid ${theme.palette.divider}`,
	backgroundColor: theme.palette.background.paper,
	backdropFilter: 'blur(10px)',
	boxShadow: theme.shadows[5],
	outline: 'none',
	overflow: 'hidden',

	[theme.breakpoints.down('sm')]: {
		width: '100%',
		height: '100%',
		maxWidth: 'none',
		minHeight: '100%',
		padding: theme.spacing(4),
		borderRadius: 0,
	},
}))

const CloseButton = styled(IconButton)(({ theme }) => ({
	position: 'absolute',
	top: theme.spacing(2),
	right: theme.spacing(2),
	color: theme.palette.text.primary,
}))

const EstateList = styled('ul')(({ theme }) => ({
	width: '100%',
	padding: 0,
	listStyle: 'none',
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(3),
	alignItems: 'center',
	maxHeight: '420px',
	overflowY: 'auto',

	'&::-webkit-scrollbar': {
		width: '4px',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: '#002D72',
		borderRadius: '4px',
	},
	'&::-webkit-scrollbar-track': {
		backgroundColor: '#f0f0f0',
	},

	[theme.breakpoints.down('sm')]: {
		maxHeight: '545px',
		overflowY: 'auto',
		paddingRight: theme.spacing(1),
	},
}))

const EstateItem = styled('li')(({ theme }) => ({
	display: 'flex',
	alignItems: 'stretch',
	justifyContent: 'flex-start',
	width: '100%',
	minHeight: '120px',
	borderRadius: theme.shape.borderRadius,
	backgroundColor: theme.palette.background.paper,
	boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
	transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
	cursor: 'pointer',
	overflow: 'hidden',
	gap: theme.spacing(3),
	paddingRight: theme.spacing(3),

	'&:hover': {
		transform: 'translateY(-4px)',
		boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.15)',
	},

	[theme.breakpoints.down('sm')]: {
		width: '320px',
		flexDirection: 'row',
		alignItems: 'center',
		gap: theme.spacing(3.5),
		minHeight: '164px',
	},
}))

const EstateImage = styled('img')(({ theme }) => ({
	width: '180px',
	height: '100%',
	objectFit: 'cover',
	borderTopLeftRadius: '8px',
	borderBottomLeftRadius: '8px',
	[theme.breakpoints.down('sm')]: {
		width: '100px',
	},
}))

const StyledImageContainer = styled('div')(({ theme }) => ({
	minWidth: '180px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: '#f0f0f0',
	borderTopLeftRadius: '8px',
	borderBottomLeftRadius: '8px',
	height: 'auto',
	[theme.breakpoints.down('sm')]: {
		minWidth: '100px',
		height: '100%',
	},
}))

const EstateContent = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'space-between',
	width: '100%',
	gap: 0,
	padding: 0,
	[theme.breakpoints.down('sm')]: {
		flexDirection: 'column',
		alignItems: 'center',
		width: '165px',
		height: '143px',
		gap: theme.spacing(1.5),
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(2.5),
	},
}))

const SelectButton = styled(Button)(({ theme }) => ({
	width: '165px',
	height: '56px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: '12px',
	gap: theme.spacing(1.5),

	[theme.breakpoints.down('sm')]: {
		width: '165px',
		height: '56px',
	},
}))

const SHORT_MODAL_HEIGHT = '320px'

const StyledShortModalBox = styled(StyledModalBox)(({ theme }) => ({
	minHeight: SHORT_MODAL_HEIGHT,
	justifyContent: 'center',
	textAlign: 'center',
	[theme.breakpoints.down('sm')]: {},
}))

const UserPropertiesModal = ({
	open,
	onClose,
	title,
	description,
	estates,
	showCloseButton = true,
}) => {
	const handleClose = (event, reason) => {
		if (reason && reason === 'backdropClick') return
		onClose(event)
	}

	const { selectEstate, users } = useLookupContext()

	const [estateImages, setEstateImages] = useState({})
	const [loadingImages, setLoadingImages] = useState(true)

	useEffect(() => {
		if (!open) return

		const fetchImages = async () => {
			setLoadingImages(true)
			const results = await Promise.all(
				estates.map(async (estate) => {
					try {
						const { data } = await client.query({
							query: SITECORE_CONTENT_QUERY,
							variables: {
								divisionCode: estate.divisionMpcCd,
								communityMPC: estate.marketingMpcCd || estate.communityMpcCd,
								planName: estate.model,
								elevationName: estate.elevation,
							},
						})

						const image = data?.planQuery?.elevationImage
						return { estateId: estate.estateId, image }
					} catch (error) {
						console.error(
							`Error fetching image for estate ${estate.estateId}`,
							error
						)
						return { estateId: estate.estateId, image: null }
					}
				})
			)

			const imageMap = {}
			results.forEach(({ estateId, image }) => {
				imageMap[estateId] = image
			})
			setEstateImages(imageMap)
			setLoadingImages(false)
		}

		fetchImages()
	}, [open, estates, client])

	const [loadingEstateId, setLoadingEstateId] = useState(null)
	const navigate = useNavigate()

	const handleSelectClick = async (estateId, uniqueUserId) => {
		try {
			setLoadingEstateId(estateId)

			await selectEstate(estateId, uniqueUserId, users)

			if (window.location.pathname === '/myProperties') {
				navigate('/home')
			} else {
				window.location.reload()
			}

			onClose()
		} catch (error) {
			console.error('Error selecting estate:', error)
		} finally {
			setLoadingEstateId(null)
		}
	}

	const allEstatesExpired =
		estates &&
		estates.length > 0 &&
		estates.every(
			(estate) =>
				estate.finalExpiryReminderEmail &&
				new Date(estate.finalExpiryReminderEmail.replace(' ', 'T')) < new Date()
		)

	if (open && (!users || users.length === 0 || allEstatesExpired)) {
		return (
			<Modal open={open} onClose={handleClose}>
				<StyledShortModalBox>
					{showCloseButton && (
						<CloseButton onClick={onClose} aria-label="Close modal">
							<CloseIcon />
						</CloseButton>
					)}
					<Typography
						component="h2"
						variant="titleMedium"
						color="primary.main"
						sx={{ marginBottom: 2 }}
					>
						It was not possible to find properties associated with the account.
					</Typography>
					<Typography variant="bodyMedium" color="textSecondary">
						You will be redirected shortly.
					</Typography>
				</StyledShortModalBox>
			</Modal>
		)
	}

	return (
		<Modal open={open} onClose={handleClose} aria-labelledby="modal-title">
			<StyledModalBox>
				{showCloseButton && (
					<CloseButton onClick={onClose} aria-label="Close modal">
						<CloseIcon />
					</CloseButton>
				)}

				<Typography
					component="h2"
					color="primary.main"
					variant="titleMedium"
					sx={{ marginBottom: 3, textAlign: 'center' }}
				>
					{title}
				</Typography>

				<Typography
					color="primary.main"
					variant="bodyMedium"
					sx={{
						textAlign: 'center',
						marginBottom: 3,
						fontSize: { xs: '16px', sm: 'inherit' },
					}}
				>
					{description}
				</Typography>

				<EstateList>
					{estates.map((estate, index) => {
						const expiryDate = estate.finalExpiryReminderEmail
							? new Date(estate.finalExpiryReminderEmail.replace(' ', 'T'))
							: null
						const isExpired = expiryDate && expiryDate < new Date()

						return (
							<EstateItem key={index}>
								<StyledImageContainer>
									{loadingImages ? (
										<Typography variant="bodySmall" color="textSecondary">
											Loading...
										</Typography>
									) : estateImages[estate.estateId]?.src ? (
										<EstateImage
											src={estateImages[estate.estateId]?.src}
											alt={estateImages[estate.estateId]?.alt || 'Estate Image'}
											onError={(e) => {
												e.target.onerror = null
												e.target.style.display = 'none'
											}}
										/>
									) : (
										<Typography variant="bodySmall" color="textSecondary">
											Image not found
										</Typography>
									)}
								</StyledImageContainer>

								<EstateContent>
									<div
										style={{
											width: '100%',
											display: 'flex',
											flexDirection: 'column',
										}}
									>
										<Typography
											variant="titleSmall"
											color="primary"
											sx={{ textAlign: 'left', width: '100%' }}
										>
											{estate.streetAddress}
										</Typography>
										{isExpired && (
											<Typography
												variant="bodySmall"
												color="error"
												sx={{
													textAlign: 'left',
													width: '100%',
													marginTop: 2,
												}}
											>
												Expired access:
												{expiryDate
													? ` ${expiryDate.toLocaleDateString('en-US', {
															year: 'numeric',
															month: 'long',
															day: 'numeric',
														})}`
													: ''}
											</Typography>
										)}
									</div>
									{!isExpired && (
										<SelectButton
											variant="outlined"
											color="primary"
											onClick={() =>
												handleSelectClick(estate.estateId, estate.uniqueUserId)
											}
											disabled={loadingEstateId === estate.estateId}
										>
											{loadingEstateId === estate.estateId
												? 'Loading...'
												: 'Select'}
										</SelectButton>
									)}
								</EstateContent>
							</EstateItem>
						)
					})}
				</EstateList>
			</StyledModalBox>
		</Modal>
	)
}

export default UserPropertiesModal
